/* eslint-disable jsx-a11y/tabindex-no-positive */
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';

import { useStoreState } from '~/store/hooks';
import formatDictionaryOptions from '~/utils/formatDictionaryOptions';
import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import AvatarUpload from '~/ui/components/inputs/AvatarUpload';
import Checkbox from '~/ui/components/inputs/Checkbox';
import DatePicker from '~/ui/components/inputs/DatePicker';
import Input from '~/ui/components/inputs/Input';
import InputMask from '~/ui/components/inputs/InputMask';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import suicideRiskOptions from '~/ui/constants/suicideRisks';
import useDictionaryOptions from '~/ui/pages/Clients/LocalAdmin/Form/hooks/useDictionaryOptions';
import { IInitialValues } from '~/ui/pages/Clients/LocalAdmin/Form/initialValues';

import styles from './PersonalDetails.module.scss';

interface IProps {
  isEditing: boolean;
}

const PersonalDetails = ({ isEditing }: IProps): ReactElement => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<IInitialValues>();

  const allActTeams = useStoreState(state => state.actTeam.allActTeams);

  const actTeamOptions = formatDictionaryOptions(allActTeams);
  const genderOptions = useDictionaryOptions(IDictionaryTypes.Gender);
  const genderIdentityOptions = useDictionaryOptions(IDictionaryTypes.GenderIdentity);
  const raceOptions = useDictionaryOptions(IDictionaryTypes.Race);

  return (
    <Paper>
      <Box sx={{ p: 2 }}>
        <p className={styles.formTitle}>Personal Details</p>
        <Grid container spacing={2}>
          <Grid
            size={{
              sm: 6,
            }}
          >
            <Grid
              container
              spacing={2}
              size={{
                sm: 12,
              }}
            >
              <Grid
                size={{
                  sm: 12,
                }}
              >
                <Input
                  tabIndex={1}
                  register={register}
                  errors={errors}
                  name="firstName"
                  label="First Name"
                />
              </Grid>
              <Grid
                size={{
                  sm: 12,
                }}
              >
                <Input
                  tabIndex={2}
                  register={register}
                  errors={errors}
                  name="lastName"
                  label="Last Name"
                />
              </Grid>
              <Grid
                size={{
                  sm: 12,
                }}
              >
                <Input
                  tabIndex={3}
                  register={register}
                  errors={errors}
                  name="preferredName"
                  label="Preferred Name"
                />
              </Grid>
              <Grid
                size={{
                  sm: 12,
                }}
              >
                <Input
                  tabIndex={4}
                  register={register}
                  errors={errors}
                  name="personalPronouns"
                  label="Personal Pronouns"
                />
              </Grid>
              {!isEditing && (
                <Grid
                  size={{
                    sm: 12,
                  }}
                >
                  <InputMask
                    tabIndex={35}
                    control={control}
                    errors={errors}
                    name="phone"
                    label="Phone"
                  />
                </Grid>
              )}
              <Grid
                size={{
                  sm: 12,
                }}
              >
                <Input
                  tabIndex={6}
                  register={register}
                  errors={errors}
                  name="email"
                  label="Email"
                />
              </Grid>

              <Grid
                size={{
                  sm: 12,
                }}
              >
                <AvatarUpload control={control} name="photo" errors={errors} isPhoto />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            size={{
              sm: 6,
            }}
          >
            <Grid
              container
              spacing={2}
              size={{
                sm: 12,
              }}
            >
              <Grid
                size={{
                  sm: 12,
                }}
              >
                <DatePicker
                  tabIndex={9}
                  control={control}
                  errors={errors}
                  label="Date of Birth"
                  name="dateOfBirth"
                  maxDate={new Date().toISOString()}
                />
              </Grid>
              <Grid
                size={{
                  sm: 6,
                }}
              >
                <Select
                  tabIndex={10}
                  options={genderOptions}
                  name="genderId"
                  control={control}
                  errors={errors}
                  label="Gender"
                  hideSelectedOptions={false}
                  openMenuOnFocus
                />
              </Grid>
              <Grid
                size={{
                  sm: 6,
                }}
              >
                <Select
                  tabIndex={10}
                  options={genderIdentityOptions}
                  name="genderIdentityId"
                  control={control}
                  errors={errors}
                  label="Gender Identity"
                  hideSelectedOptions={false}
                  openMenuOnFocus
                />
              </Grid>
              <Grid
                size={{
                  sm: 12,
                }}
              >
                <Select
                  tabIndex={11}
                  options={raceOptions}
                  name="raceId"
                  control={control}
                  errors={errors}
                  label="Race"
                  hideSelectedOptions={false}
                  openMenuOnFocus
                />
              </Grid>
              <Grid
                size={{
                  sm: 12,
                }}
              >
                <Select
                  tabIndex={12}
                  options={suicideRiskOptions}
                  name="suicideRisk"
                  control={control}
                  errors={errors}
                  label="Suicide Risk"
                  hideSelectedOptions={false}
                  openMenuOnFocus
                />
              </Grid>
              <Grid
                size={{
                  sm: 12,
                }}
              >
                <Select
                  tabIndex={12}
                  options={actTeamOptions}
                  name="teamId"
                  control={control}
                  errors={errors}
                  label="Assigned Team"
                  isDisabled={isEditing}
                  hideSelectedOptions={false}
                />
              </Grid>
              <Grid
                size={{
                  sm: 12,
                }}
              >
                <Input
                  tabIndex={13}
                  register={register}
                  errors={errors}
                  name="emrMrn"
                  label="EMR MRN"
                />
              </Grid>

              <Grid
                size={{
                  sm: 12,
                }}
              >
                <Checkbox size="small" name="aot" control={control} errors={errors} label="AOT" />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default PersonalDetails;
